import { z } from 'zod';
import {
  CustomContext,
  VIEW,
  WidgetActions,
  WidgetActionsObject,
} from '@/types';
import { WidgetStoreState } from './stores/use-widget-store';
import { StoreApi } from 'zustand';

export type ActionFunctionParams = {
  action: WidgetActions;
} & WidgetActionsObject;

type ActionFunction = (
  params: ActionFunctionParams & { store: WidgetStoreState }
) => void | Promise<void>;

type WidgetActionMap = Record<WidgetActions, ActionFunction>;

// Validation schemas for action data
const QuestionnaireDataSchema = z.record(z.string(), z.string());
const CustomContextSchema = z
  .object({
    proposalId: z.string().optional(),
    proposalTitle: z.string().optional(),
    expertId: z.string().optional(),
    clientName: z.string().optional(),
    clientEmail: z.string().optional(),
  })
  .passthrough(); // Allow additional properties
const SendMessageDataSchema = z.object({
  message: z.string().min(1),
});

const WIDGET_ACTION_METHODS: WidgetActionMap = {
  'open-widget': ({ store }) => {
    store.interface.setWidgetIsOpen(true);
  },
  'open-chat': ({ store }) => {
    store.interface.setWidgetIsOpen(true);
    store.interface.setWidgetActiveView(VIEW.CHAT);
  },
  'set-view-chat': ({ store }) => {
    store.interface.setWidgetActiveView(VIEW.CHAT);
  },
  'set-questionnaire-field-values': ({ store, data }) => {
    const result = QuestionnaireDataSchema.safeParse(data);
    if (!result.success) {
      console.error(
        '[Widget Actions] Invalid questionnaire data:',
        result.error
      );
      return;
    }
    store.chat.setFilledQuestionnaireData(result.data);
  },
  'set-custom-context': ({ store, data }) => {
    const result = CustomContextSchema.safeParse(data);
    if (!result.success) {
      console.error('[Widget Actions] Invalid custom context:', result.error);
      return;
    }
    store.context.setCustomContext(result.data as CustomContext);
  },
  'clear-session': ({ store }) => {
    store.chat.clearSession();
    // Note: Zustand persist middleware will handle localStorage cleanup automatically
  },
  'start-direct-chat': ({ store }) => {
    const { customContext } = store.context;
    const { sessionId } = store.chat;
    const isProposalMode = !!customContext.proposalId;

    // If we already have a session, just open the chat (user is returning)
    if (sessionId) {
      store.interface.setWidgetIsOpen(true);
      store.interface.setWidgetActiveView(VIEW.CHAT);
      return;
    }

    // For new sessions:
    // - Proposal mode: No validation needed, backend has contact data
    // - New inquiry mode: Contact info required
    if (
      !isProposalMode &&
      (!customContext.clientName || !customContext.clientEmail)
    ) {
      console.error(
        '[Widget] start-direct-chat requires clientName and clientEmail in customContext for new inquiries'
      );
      return;
    }

    // Set formData only if we have contact info (NewInquiry mode)
    // ProposalConversation mode doesn't need it - backend has contact data
    if (customContext.clientName && customContext.clientEmail) {
      const formData = {
        name: customContext.clientName as string,
        email: customContext.clientEmail as string,
      };
      store.chat.setFilledQuestionnaireData(formData);
    }

    // Open widget and switch to chat view
    // For proposal mode, this will trigger the auto-initialization flow
    store.interface.setWidgetIsOpen(true);
    store.interface.setWidgetActiveView(VIEW.CHAT);
  },
  'send-message': async ({ data }) => {
    const result = SendMessageDataSchema.safeParse(data);
    if (!result.success) {
      console.error(
        '[Widget Actions] Invalid send-message data:',
        result.error
      );
      return;
    }

    // Note: This action requires a sendMessage callback to be injected
    // The callback will be provided when creating the action handlers
    console.error(
      '[Widget Actions] send-message action requires sendMessageCallback to be injected'
    );
  },
};

export interface WidgetActionHandlers {
  executeAction: (params: ActionFunctionParams) => void | Promise<void>;
  setFilledQuestionnaireData: (
    filledQuestionnaireData: Record<string, string>
  ) => void;
  setCustomContext: (customContext: CustomContext) => void;
}

export interface CreateWidgetActionHandlersOptions {
  store: StoreApi<WidgetStoreState>;
  sendMessageCallback?: (message: string) => Promise<void>;
}

export const createWidgetActionHandlers = (
  options: CreateWidgetActionHandlersOptions
): WidgetActionHandlers => {
  const { store, sendMessageCallback } = options;

  // Create a modified action map with sendMessage callback injected
  const actionMap: WidgetActionMap = {
    ...WIDGET_ACTION_METHODS,
    'send-message': async ({ data }) => {
      const result = SendMessageDataSchema.safeParse(data);
      if (!result.success) {
        console.error(
          '[Widget Actions] Invalid send-message data:',
          result.error
        );
        return;
      }

      if (!sendMessageCallback) {
        console.error(
          '[Widget Actions] send-message action requires sendMessageCallback to be provided'
        );
        return;
      }

      await sendMessageCallback(result.data.message);
    },
  };

  const handleExecuteAction = (params: ActionFunctionParams) => {
    actionMap[params.action]({
      store: store.getState(),
      ...params,
    });
  };

  const handleSetFilledQuestionnaireData = (
    filledQuestionnaireData: Record<string, string>
  ) => {
    store.getState().chat.setFilledQuestionnaireData(filledQuestionnaireData);
  };

  const handleSetCustomContext = (customContext: CustomContext) => {
    store.getState().context.setCustomContext(customContext);
  };

  return {
    executeAction: handleExecuteAction,
    setFilledQuestionnaireData: handleSetFilledQuestionnaireData,
    setCustomContext: handleSetCustomContext,
  };
};
